import { createContext, useContext, useMemo } from 'react';
import { useInfiniteQuery, useQuery } from 'react-query';

import EquipmentStatus from '@models/EquipmentStatus.model';

import { State, useState, createState } from '@hookstate/core';

import { useHistory, useLocation, useParams } from 'react-router-dom';
import { getFileByFileId } from '@services/queries/onboarding/getFileByFileId';

import { NavigationContext } from '@layout/navigation/navigation';
import {
  getGroupedAssetsWithSearch,
  getGroupedIdentifiedAssetsWithSearch,
} from '@services/queries/onboarding/getGroupedAssetsWithSearch';
import { IOnboardingGroup } from '@models/OnboardingGroup.model';
import { searchAssets } from '@services/mixpanel/trackers/Onboarding';

import { OnboardingFileLoading } from './OnboardingFile.loading';
import { OnboardingFileRaw } from './OnboardingFile.raw';

export type SortOnboardingFileTypes =
  | 'equipment_status.name'
  | 'equipment_status.description'
  | 'equipment_status.category'
  | undefined;

export type DirectionOnboardingFiletypes = 'ASC' | 'DESC' | undefined;
export type FieldErrorTypes =
  | 'manufacturer'
  | 'productName'
  | 'model'
  | undefined;

interface IOnboardingFileContext {
  openCreateDialog: () => void;
  openUpdateDialog: (status: EquipmentStatus) => void;
  openDeleteDialog: (status: EquipmentStatus) => void;
  equipmentStatus: EquipmentStatus;
  openDelete: State<boolean>;
  errorDialog: State<boolean>;
  errorManufacturerDialog: State<boolean>;
  fieldErrorSelected: State<FieldErrorTypes>;
  accessoryDialog: State<boolean>;
  openChat: State<boolean>;
  selectedGroup: State<IOnboardingGroup | null>;
  selectedProductId: State<string | null>;
  search: State<string>;
  sort: State<SortOnboardingFileTypes>;
  direction: State<DirectionOnboardingFiletypes>;
  status: string;
}

export const OnboardingFileContext = createContext<IOnboardingFileContext>({
  openCreateDialog: () => {},
  openUpdateDialog: () => {},
  openDeleteDialog: () => {},
  equipmentStatus: EquipmentStatus.createEmpty(),
  openDelete: createState<boolean>(false),
  errorDialog: createState<boolean>(false),
  errorManufacturerDialog: createState<boolean>(false),
  fieldErrorSelected: createState<FieldErrorTypes>(undefined),
  accessoryDialog: createState<boolean>(false),
  openChat: createState<boolean>(false),
  selectedGroup: createState<IOnboardingGroup | null>(null),
  selectedProductId: createState<string | null>(''),
  search: createState<string>(''),
  sort: createState<SortOnboardingFileTypes>(undefined),
  direction: createState<DirectionOnboardingFiletypes>(undefined),
  status: 'unverified',
});

const useInfiniteQueryParams = () => {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
};

const OnboardingFileQuery = () => {
  const params = useParams<{ id: string }>();

  const { studioId, studioName } = useContext(NavigationContext);

  const history = useHistory();

  const openCreateDialog = useState<boolean>(false);
  const openEditDialog = useState<boolean>(false);
  const openDeleteDialog = useState<boolean>(false);
  const errorDialog = useState<boolean>(false);
  const errorManufacturerDialog = useState<boolean>(false);
  const fieldErrorSelected = useState<FieldErrorTypes>(undefined);
  const accessoryDialog = useState<boolean>(false);
  const openChat = useState<boolean>(false);
  const selectedGroup = useState<IOnboardingGroup | null>(null);
  const selectedProductId = useState<string | null>('');
  const search = useState<string>('');
  const sort = useState<SortOnboardingFileTypes>(undefined);
  const direction = useState<DirectionOnboardingFiletypes>(undefined);
  const hasData = useState<boolean>(false);

  const equipmentStatus = useState<EquipmentStatus>(
    EquipmentStatus.createEmpty(),
  );

  const query = useInfiniteQueryParams();

  const status = query.get('tab');

  if (
    !status ||
    ![
      'identified',
      'unidentified',
      'identification_requested',
      'inventory',
      'approx_match',
    ].includes(status)
  ) {
    history.push(`/onboarding/${params.id}?tab=approx_match`);
  }

  const { data, isLoading, isError } = useQuery(
    ['onboarding', 'files', params.id],
    () =>
      getFileByFileId({
        studioId,
        fileId: params.id,
      }),
    {
      onSuccess: () => {
        hasData.set(true);
      },
    },
  );

  const identifiedGroupsQuery = useInfiniteQuery(
    [
      'identifiedonboarding',
      'files',
      params.id,
      'assets',
      search.get(),
      status,
    ],
    async ({ pageParam = 1 }) => {
      return getGroupedIdentifiedAssetsWithSearch({
        studioId,
        fileId: params.id,
        perPage: 20,
        page: pageParam,
        search: search.get(),
      });
    },
    {
      getPreviousPageParam: undefined,
      getNextPageParam: lastPage =>
        !lastPage.isLastPage ? lastPage.currentPage + 1 : undefined,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      onSuccess: results => {
        if (search.get()) {
          searchAssets(
            studioName,
            search.get(),
            results.pages[0].totalAssets > 0,
          );
        }
      },
      enabled: status === 'identified',
    },
  );

  const groupsQuery = useInfiniteQuery(
    ['onboarding', 'files', params.id, 'assets', search.get(), status],
    async ({ pageParam = 1 }) => {
      return getGroupedAssetsWithSearch({
        studioId,
        fileId: params.id,
        perPage: 20,
        page: pageParam,
        status,
        search: search.get(),
      });
    },
    {
      getPreviousPageParam: undefined,
      getNextPageParam: lastPage =>
        !lastPage.isLastPage ? lastPage.currentPage + 1 : undefined,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      onSuccess: results => {
        if (search.get()) {
          searchAssets(
            studioName,
            search.get(),
            results.pages[0].totalAssets > 0,
          );
        }
      },
      enabled: status !== 'identified',
    },
  );

  if (!status) {
    return <OnboardingFileLoading />;
  }

  return (
    <OnboardingFileContext.Provider
      value={{
        openDeleteDialog: eStatus => {
          equipmentStatus.set(eStatus);
          openDeleteDialog.set(true);
        },
        openUpdateDialog: eStatus => {
          equipmentStatus.set(eStatus);
          openEditDialog.set(true);
        },
        openCreateDialog: () => openCreateDialog.set(true),
        equipmentStatus: equipmentStatus.get(),
        openDelete: openDeleteDialog,
        search,
        sort,
        direction,
        status,
        errorDialog,
        selectedGroup,
        selectedProductId,
        accessoryDialog,
        fieldErrorSelected,
        errorManufacturerDialog,
        openChat,
      }}
    >
      {status === 'identified' ? (
        <OnboardingFileRaw
          identifiedGroupsQuery={identifiedGroupsQuery.data}
          fileData={data}
          fetchNextPage={identifiedGroupsQuery.fetchNextPage}
          isError={isError || identifiedGroupsQuery.isError}
          isLoading={isLoading || identifiedGroupsQuery.isLoading}
        />
      ) : (
        <OnboardingFileRaw
          groupsQuery={groupsQuery.data}
          fileData={data}
          fetchNextPage={groupsQuery.fetchNextPage}
          isError={isError || groupsQuery.isError}
          isLoading={isLoading || groupsQuery.isLoading}
        />
      )}
    </OnboardingFileContext.Provider>
  );
};

export { OnboardingFileQuery };
